import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Select, { SingleValue } from 'react-select';
import { Home } from 'lucide-react';
import { CollapsibleSection } from './CollapsibleSection';
import { getBlockLabel, getUnitLabel, CondoType } from '../../utils/terminology';
import { condoApi } from '../../services/api';
import { ParentescoOptionsSelect } from './ParentescoField';

interface Block {
  id: string;
  name: string;
}

interface Unit {
  id: string;
  number: string;
  block_id: string;
}

interface UnitAssignmentSectionProps {
  blocks: Block[];
  units: Unit[];
  selectedBlockId: string;
  selectedUnitId: string;
  relationType: string;
  startDate: string;
  /** Optional "grau de parentesco" — a free-form kinship note shown for any relation type. */
  parentesco?: string;
  onBlockChange: (blockId: string) => void;
  onUnitChange: (unitId: string) => void;
  onRelationTypeChange: (type: string) => void;
  onParentescoChange?: (parentesco: string) => void;
  onStartDateChange: (date: string) => void;
  disabled?: boolean;
  darkMode?: boolean;
  condoType?: CondoType;
  customerId?: string;
  /** Show the "Tipo de Relação" (proprietário/inquilino/etc.) + "Data Início" sub-block. Defaults
   * to true (residents). Set false for person categories with no ownership-relation concept
   * (e.g. condo employees) — the unit/block pickers stay, but relation type is hidden and not required. */
  showRelationType?: boolean;
}

interface FlatUnit {
  id: string;
  number: string;
  block_name: string;
  block_id: string;
  street_name?: string;
  street_id?: string;
  street_number?: string;
}

const RELATION_TYPES = [
  { value: 'proprietario', label: 'Proprietário' },
  { value: 'inquilino', label: 'Inquilino' },
  { value: 'dependente', label: 'Dependente' },
  { value: 'familiar', label: 'Familiar' },
];

type SelectOpt = SingleValue<{ value: string; label: string }>;

const selectClassNames = (darkMode: boolean) => ({
  control: (state: any) =>
    `!shadow-sm !rounded-lg !min-h-[36px] ${darkMode
      ? `!bg-gray-800 !border-gray-600 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`
      : `!border-gray-300 ${state.isFocused ? '!border-primary-500 !ring-1 !ring-primary-500' : ''}`}`,
  input: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
  option: (state: any) =>
    `!text-sm ${darkMode
      ? state.isSelected ? '!bg-primary-600 !text-white' : state.isFocused ? '!bg-gray-700 !text-white' : '!bg-gray-800 !text-gray-200'
      : state.isSelected ? '!bg-primary-600 !text-white' : state.isFocused ? '!bg-gray-100 !text-gray-900' : '!text-gray-900'}`,
  placeholder: () => `!text-sm ${darkMode ? '!text-gray-500' : '!text-gray-400'}`,
  singleValue: () => `!text-sm ${darkMode ? '!text-white' : '!text-gray-900'}`,
  menu: () => darkMode ? '!bg-gray-800 !border-gray-600' : '!bg-white',
  menuList: () => darkMode ? '!bg-gray-800' : '!bg-white',
});

const selectStyles = (darkMode: boolean) => ({
  control: (base: any) => ({ ...base, minHeight: '36px', backgroundColor: darkMode ? '#1f2937' : base.backgroundColor }),
  menu: (base: any) => ({ ...base, zIndex: 9999, backgroundColor: darkMode ? '#1f2937' : base.backgroundColor }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: darkMode
      ? state.isSelected ? '#7c3aed' : state.isFocused ? '#374151' : '#1f2937'
      : state.isSelected ? '#7c3aed' : state.isFocused ? '#f3f4f6' : '#ffffff',
    color: darkMode ? '#e5e7eb' : state.isSelected ? '#ffffff' : '#111827',
  }),
});

export const UnitAssignmentSection: React.FC<UnitAssignmentSectionProps> = ({
  blocks,
  units,
  selectedBlockId,
  selectedUnitId,
  relationType,
  startDate,
  parentesco = '',
  onBlockChange,
  onUnitChange,
  onRelationTypeChange,
  onParentescoChange,
  onStartDateChange,
  disabled = false,
  darkMode = false,
  condoType,
  customerId,
  showRelationType = true,
}) => {
  // All flat units for street-condo mode (loaded from searchUnits endpoint)
  const [allFlatUnits, setAllFlatUnits] = useState<FlatUnit[]>([]);
  const [loadingFlatUnits, setLoadingFlatUnits] = useState(false);
  const [hasStreets, setHasStreets] = useState(false);

  // Street condo local filter state
  const [selectedStreet, setSelectedStreet] = useState<SelectOpt>(null);
  const [streetNumberSearch, setStreetNumberSearch] = useState('');
  const [localBlockFilter, setLocalBlockFilter] = useState<SelectOpt>(null);

  // Probe for streets by loading first page of units
  useEffect(() => {
    if (!customerId) return;
    setLoadingFlatUnits(true);
    condoApi.searchUnits(customerId, '', { limit: 5000 })
      .then((res: any) => {
        const data: FlatUnit[] = res.data?.data || [];
        setAllFlatUnits(data);
        setHasStreets(data.some((u) => u.street_name));
      })
      .catch(() => setAllFlatUnits([]))
      .finally(() => setLoadingFlatUnits(false));
  }, [customerId]);

  const isStreetCondo = hasStreets;

  // Unique streets from flat units
  const streetOptions = useMemo(() => {
    const seen = new Map<string, string>(); // street_id → name
    allFlatUnits.forEach((u) => {
      if (u.street_id && u.street_name) seen.set(u.street_id, u.street_name);
    });
    return Array.from(seen.entries())
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { numeric: true }));
  }, [allFlatUnits]);

  // Street-filtered units
  const streetFilteredUnits = useMemo(() => {
    if (!selectedStreet) return allFlatUnits;
    return allFlatUnits.filter((u) => u.street_id === selectedStreet.value);
  }, [allFlatUnits, selectedStreet]);

  // Unique street numbers from street-filtered units
  const numberOptions = useMemo(() => {
    const seen = new Set<string>();
    streetFilteredUnits.forEach((u) => { if (u.street_number) seen.add(u.street_number); });
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }))
      .map((n) => ({ value: n, label: n }));
  }, [streetFilteredUnits]);

  // Number-filtered units
  const numberFilteredUnits = useMemo(() => {
    if (!streetNumberSearch) return streetFilteredUnits;
    return streetFilteredUnits.filter((u) => (u.street_number || '') === streetNumberSearch);
  }, [streetFilteredUnits, streetNumberSearch]);

  // Unique blocks from number-filtered units
  const blockFilterOptions = useMemo(() => {
    const seen = new Map<string, string>(); // block_id → name
    numberFilteredUnits.forEach((u) => { if (u.block_id) seen.set(u.block_id, u.block_name); });
    return Array.from(seen.entries())
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { numeric: true }));
  }, [numberFilteredUnits]);

  // Block-filtered units (lote options)
  const loteOptions = useMemo(() => {
    let filtered = numberFilteredUnits;
    if (localBlockFilter) filtered = filtered.filter((u) => u.block_id === localBlockFilter.value);
    return filtered
      .sort((a, b) => a.number.localeCompare(b.number, 'pt-BR', { numeric: true }))
      .map((u) => ({ value: u.id, label: u.number }));
  }, [numberFilteredUnits, localBlockFilter]);

  // Selected lote option (derived from selectedUnitId)
  const selectedLoteOption = useMemo(() => {
    if (!selectedUnitId) return null;
    const u = allFlatUnits.find((x) => x.id === selectedUnitId);
    return u ? { value: u.id, label: u.number } : null;
  }, [selectedUnitId, allFlatUnits]);

  const handleStreetChange = useCallback((opt: SelectOpt) => {
    setSelectedStreet(opt);
    setStreetNumberSearch('');
    setLocalBlockFilter(null);
    onUnitChange('');
    onBlockChange('');
  }, [onUnitChange, onBlockChange]);

  const handleNumberChange = useCallback((opt: SelectOpt) => {
    const num = opt?.value || '';
    setStreetNumberSearch(num);
    onUnitChange('');
    onBlockChange('');
    setLocalBlockFilter(null);
    if (num && selectedStreet) {
      // Auto-select the unit for this street+number combo
      const match = allFlatUnits.find(
        (u) => u.street_id === selectedStreet.value && (u.street_number || '') === num
      );
      if (match) {
        onUnitChange(match.id);
        onBlockChange(match.block_id);
        setLocalBlockFilter({ value: match.block_id, label: match.block_name });
      }
    }
  }, [allFlatUnits, selectedStreet, onUnitChange, onBlockChange]);

  const handleLoteChange = useCallback((opt: SelectOpt) => {
    if (!opt) {
      onUnitChange('');
      onBlockChange('');
      return;
    }
    const unit = allFlatUnits.find((u) => u.id === opt.value);
    onUnitChange(opt.value);
    if (unit) {
      onBlockChange(unit.block_id);
      setLocalBlockFilter({ value: unit.block_id, label: unit.block_name });
      if (unit.street_id) {
        setSelectedStreet({ value: unit.street_id, label: unit.street_name || '' });
        setStreetNumberSearch(unit.street_number || '');
      }
    }
  }, [allFlatUnits, onUnitChange, onBlockChange]);

  // Block-condo state
  const filteredUnits = selectedBlockId ? units.filter((u) => u.block_id === selectedBlockId) : [];
  const sortedBlocks = [...blocks].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { numeric: true }));
  const sortedUnits = [...filteredUnits].sort((a, b) => a.number.localeCompare(b.number, 'pt-BR', { numeric: true }));

  // Auto-select block if there's only one (block condos only)
  useEffect(() => {
    if (!isStreetCondo && blocks.length === 1 && !selectedBlockId) {
      onBlockChange(blocks[0].id);
    }
  }, [blocks, selectedBlockId, onBlockChange, isStreetCondo]);

  const isRelationTypeRequired = showRelationType && !!selectedUnitId;
  const relationTypeError = isRelationTypeRequired && !relationType;

  const inputClass = `w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
    darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border border-gray-300'
  } ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`;

  const labelClass = `block text-[11px] font-semibold tracking-widest uppercase mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`;

  return (
    <CollapsibleSection
      title={`Vincular ${getUnitLabel(condoType)}`}
      icon={Home}
      badge="Opcional"
      defaultOpen={true}
      darkMode={darkMode}
    >
      <div className="pt-3 space-y-3">
        {loadingFlatUnits ? (
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Carregando...</p>
        ) : isStreetCondo ? (
          /* Street condo: 4 cascading filters */
          <>
            {/* Row 1: Rua + Número */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Rua <span className="opacity-60">(Filtro)</span></label>
                <Select
                  options={streetOptions}
                  value={selectedStreet}
                  onChange={handleStreetChange}
                  placeholder="Selecione uma rua..."
                  noOptionsMessage={() => "Nenhuma rua encontrada"}
                  isClearable
                  isSearchable
                  isDisabled={disabled}
                  classNames={selectClassNames(darkMode)}
                  styles={selectStyles(darkMode)}
                />
              </div>
              <div>
                <label className={labelClass}>Número <span className="opacity-60">(Filtro)</span></label>
                <Select
                  options={numberOptions}
                  value={streetNumberSearch ? { value: streetNumberSearch, label: streetNumberSearch } : null}
                  onChange={handleNumberChange}
                  placeholder="Selecione um número..."
                  noOptionsMessage={() => selectedStreet ? "Nenhum número encontrado" : "Selecione uma rua primeiro"}
                  isClearable
                  isSearchable
                  isDisabled={disabled}
                  classNames={selectClassNames(darkMode)}
                  styles={selectStyles(darkMode)}
                />
              </div>
            </div>

            {/* Row 2: Quadra (filter) + Lote (selection) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Quadra <span className="opacity-60">(Filtro)</span></label>
                <Select
                  options={blockFilterOptions}
                  value={localBlockFilter}
                  onChange={(opt) => {
                    setLocalBlockFilter(opt);
                    onUnitChange('');
                    onBlockChange('');
                  }}
                  placeholder="Selecione uma quadra..."
                  noOptionsMessage={() => "Nenhuma quadra encontrada"}
                  isClearable
                  isSearchable
                  isDisabled={disabled}
                  classNames={selectClassNames(darkMode)}
                  styles={selectStyles(darkMode)}
                />
              </div>
              <div>
                <label className={labelClass}>Lote *</label>
                <Select
                  options={loteOptions}
                  value={selectedLoteOption}
                  onChange={handleLoteChange}
                  placeholder="Buscar lote..."
                  noOptionsMessage={() => "Nenhum lote encontrado"}
                  isClearable
                  isSearchable
                  isDisabled={disabled}
                  classNames={selectClassNames(darkMode)}
                  styles={selectStyles(darkMode)}
                />
              </div>
            </div>
          </>
        ) : (
          /* Block condo: Bloco + Unidade */
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {getBlockLabel(condoType)}
              </label>
              <select
                value={selectedBlockId}
                onChange={(e) => { onBlockChange(e.target.value); onUnitChange(''); }}
                disabled={disabled}
                className={inputClass}
              >
                <option value="">Selecionar {getBlockLabel(condoType).toLowerCase()}</option>
                {sortedBlocks.map((block) => (
                  <option key={block.id} value={block.id}>{block.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {getUnitLabel(condoType)}
              </label>
              <select
                value={selectedUnitId}
                onChange={(e) => onUnitChange(e.target.value)}
                disabled={disabled || !selectedBlockId}
                className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border border-gray-300'
                } ${(disabled || !selectedBlockId) ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
              >
                <option value="">Selecionar {getUnitLabel(condoType).toLowerCase()}</option>
                {sortedUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>{unit.number}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Relation Type + Start Date */}
        {showRelationType && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Tipo de Relação
                {isRelationTypeRequired && <span className="text-red-500 ml-1">*</span>}
              </label>
              <select
                value={relationType}
                onChange={(e) => onRelationTypeChange(e.target.value)}
                disabled={disabled}
                required={isRelationTypeRequired}
                className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 ${
                  relationTypeError
                    ? 'border-2 border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'focus:ring-primary-500 focus:border-primary-500'
                } ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border border-gray-300'} ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
              >
                <option value="">Selecionar tipo</option>
                {RELATION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              {relationTypeError && (
                <p className="text-red-500 text-xs mt-1">
                  Tipo de relação é obrigatório ao selecionar uma {isStreetCondo ? 'unidade' : getUnitLabel(condoType).toLowerCase()}
                </p>
              )}
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Data Início
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => onStartDateChange(e.target.value)}
                disabled={disabled}
                className={inputClass}
              />
            </div>
            <div className="col-span-2">
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Grau de Parentesco <span className="opacity-60">(opcional)</span>
              </label>
              <div className="flex flex-col gap-2">
                <ParentescoOptionsSelect
                  value={parentesco || ''}
                  onChange={(v) => onParentescoChange?.(v)}
                  className={inputClass}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        )}

        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          A vinculação de {getUnitLabel(condoType).toLowerCase()} pode ser feita posteriormente na aba de moradores.
        </p>
      </div>
    </CollapsibleSection>
  );
};

export default UnitAssignmentSection;
